import { useNavigate } from 'react-router';
import type { TicketBoard } from '../types';
import { DeadlineDot } from '../ui/DeadlineDot';
import { TicketAvatar } from '../ui/TicketAvatar';
import { TicketPill } from '../ui/TicketPill';
import styles from './board.module.css';

/**
 * Backoffice 04 (`?filter=exceeded`): the escalation banner and the table Nr. · Betreff · Kategorie · Zuständig ·
 * Frist · Eskaliert an. A row opens the record (no inline actions).
 */
export function ExceededTable({ exceeded }: { exceeded: NonNullable<TicketBoard['exceeded']> }) {
  const navigate = useNavigate();
  return (
    <>
      <div role="status" className={styles.exceededBanner}>
        <span className={styles.exceededTitle}>{exceeded.banner.title}</span>
        <span className={styles.exceededText}>{exceeded.banner.text}</span>
      </div>
      <div className={styles.tableCard}>
        <div className={styles.tableInner} role="table" aria-label="Tickets über Frist">
          <div className={styles.tableHead} role="row">
            <span role="columnheader">Nr.</span>
            <span role="columnheader">Betreff</span>
            <span role="columnheader">Kategorie</span>
            <span role="columnheader">Zuständig</span>
            <span role="columnheader">Frist</span>
            <span role="columnheader">Eskaliert an</span>
          </div>
          {exceeded.rows.length === 0 ? (
            <div role="row" className={styles.emptyTable}>
              <span role="cell">Kein Ticket ist über der Frist.</span>
            </div>
          ) : null}
          {exceeded.rows.map((row) => (
            <div
              key={row.id}
              role="row"
              tabIndex={0}
              className={styles.tableRow}
              onClick={() => void navigate(`/tickets/${row.id}`)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  void navigate(`/tickets/${row.id}`);
                }
              }}
            >
              <span role="cell" className={styles.rowNumber}>
                {row.numberLabel}
              </span>
              <span role="cell" className={styles.rowSubject}>
                <span className={styles.rowTitle}>{row.title}</span>
                <span className={styles.rowContact}>{row.contactName}</span>
              </span>
              <span role="cell" className={styles.rowCell}>
                {row.category ? <TicketPill label={row.category} tone="neutral" /> : null}
              </span>
              <span role="cell" className={styles.rowPerson}>
                {row.assigneeShort ? (
                  <>
                    {row.assigneeInitials ? (
                      <TicketAvatar initials={row.assigneeInitials} size={20} tone="dark" />
                    ) : null}
                    <span className={styles.nowrap}>{row.assigneeShort}</span>
                  </>
                ) : (
                  <span className={styles.unassigned}>unzugeordnet</span>
                )}
              </span>
              <span role="cell" className={styles.rowDeadline}>
                <DeadlineDot tone="danger" />
                {row.deadlineLabel}
              </span>
              <span role="cell" className={styles.rowPerson}>
                {row.escalatedLabel ? (
                  <>
                    {row.escalatedInitials ? (
                      <TicketAvatar initials={row.escalatedInitials} size={20} tone="light" />
                    ) : null}
                    <span className={styles.nowrap}>{row.escalatedLabel}</span>
                  </>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
